import React from 'react';
import { useDispatch } from 'react-redux';
import type { Artifact } from '../../api/artifacts/interface';
import type { PolicyClaim } from '../../api/policyClaims/interface';
import { useSelector } from '../store';
import { actions as claimsActions } from './logic';

const EMPTY_CLAIMS: PolicyClaim[] = [];
const EMPTY_ARTIFACTS: Artifact[] = [];

interface ClaimsHookState {
  claims: PolicyClaim[];
  hasData: boolean;
  status: string;
}

export function usePolicyClaimsHook(
  dossierId: string
): [ClaimsHookState, { fetchClaims: () => void }] {
  const dispatch = useDispatch();
  const claims = useSelector(
    (s) => s.policyClaims.byDossierId[dossierId] ?? EMPTY_CLAIMS
  );
  const hasData = useSelector(
    (s) => s.policyClaims.byDossierId[dossierId] !== undefined
  );
  const status = useSelector(
    (s) => s.policyClaims.claimsStatusByDossierId[dossierId] ?? 'uninitialized'
  );

  const handler = React.useMemo(
    () => ({
      fetchClaims: () => {
        dispatch(claimsActions.fetchClaims(dossierId));
      },
    }),
    [dispatch, dossierId]
  );

  return [{ claims, hasData, status }, handler];
}

interface ClaimArtifactsHookState {
  artifacts: Artifact[];
  hasData: boolean;
  status: string;
}

export function useClaimArtifactsHook(
  claimId: string
): [ClaimArtifactsHookState, { fetchClaimArtifacts: () => void }] {
  const dispatch = useDispatch();
  const artifacts = useSelector(
    (s) => s.policyClaims.artifactsByClaimId[claimId] ?? EMPTY_ARTIFACTS
  );
  const hasData = useSelector(
    (s) => s.policyClaims.artifactsByClaimId[claimId] !== undefined
  );
  const status = useSelector(
    (s) => s.policyClaims.artifactsStatusByClaimId[claimId] ?? 'uninitialized'
  );

  const handler = React.useMemo(
    () => ({
      fetchClaimArtifacts: () => {
        if (claimId) {
          dispatch(claimsActions.fetchClaimArtifacts(claimId));
        }
      },
    }),
    [dispatch, claimId]
  );

  return [{ artifacts, hasData, status }, handler];
}
